import { create } from 'zustand';

import type { Call, ChatUser } from '../types';

/**
 * `idle` – no call · `starting` – asking for media / the server · `active` – in a call (ringing until somebody joins,
 * see `call.connectedAt`).
 */
export type CallStatus = 'idle' | 'starting' | 'active';

/** A remote participant: their signaling connection, media flags and the WebRTC state. */
export interface RemotePeer {
  connectionId: string;
  user: ChatUser;
  audio: boolean;
  video: boolean;
  screen: boolean;
  /** Tracks received from them (null until the first track arrives). */
  stream: MediaStream | null;
  /** Bumped when tracks are added, so views re-attach the stream. */
  streamVersion: number;
  state: RTCPeerConnectionState | 'waiting';
}

export interface CallStoreState {
  /** Calls ringing for the current user. */
  incoming: Call[];
  status: CallStatus;
  call: Call | null;
  /** This tab's signaling connection id. */
  connectionId: string | null;
  localStream: MediaStream | null;
  localVersion: number;
  audioEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
  peers: Record<string, RemotePeer>;
  minimized: boolean;

  addIncoming: (call: Call) => void;
  updateIncoming: (call: Call, me: string) => void;
  removeIncoming: (callId: string) => void;
  setMinimized: (minimized: boolean) => void;
}

const initial = {
  status: 'idle' as CallStatus,
  call: null,
  connectionId: null,
  localStream: null,
  localVersion: 0,
  audioEnabled: true,
  videoEnabled: false,
  screenSharing: false,
  peers: {},
  minimized: false,
};

export const useCallStore = create<CallStoreState>()((set) => ({
  incoming: [],
  ...initial,
  addIncoming: (call) =>
    set((s) =>
      s.incoming.some((c) => c.id === call.id) || s.call?.id === call.id
        ? s
        : { incoming: [...s.incoming, call] },
    ),
  updateIncoming: (call, me) =>
    set((s) => {
      if (!s.incoming.some((c) => c.id === call.id)) return s;
      const answeredElsewhere =
        call.participants.some((p) => p.user.id === me) || call.declinedUserIds.includes(me);
      return {
        incoming: answeredElsewhere
          ? s.incoming.filter((c) => c.id !== call.id)
          : s.incoming.map((c) => (c.id === call.id ? call : c)),
      };
    }),
  removeIncoming: (callId) => set((s) => ({ incoming: s.incoming.filter((c) => c.id !== callId) })),
  setMinimized: (minimized) => set({ minimized }),
}));

/** Resets the current call (not the incoming list). */
export const resetCall = () => useCallStore.setState({ ...initial });

export const callState = () => useCallStore.getState();
